import { FUNDING_REQUEST_CONTRACT_TYPES } from "helpers/constantTypes";

export function canBrokerEditApprovedFactoredFR(options: {
  isBroker: boolean;
  status?: string;
  debtorName?: string | null;
  isNonFactoredFR: boolean;
}): boolean {
  const { isBroker, status, debtorName, isNonFactoredFR } = options;

  if (!isBroker) return false;
  if (isNonFactoredFR) return false;

  const isApproved = ["Approved", "Remote Approved"].includes(status ?? "");
  if (!isApproved) return false;

  if (debtorName === "No Debtor") return false;

  return true;
}

const SELF_FINANCE_CONTRACT_TYPES = [
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM
];

export function canBrokerDeclineApprovedSelfFinanceFR(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  canBrokerDecline?: boolean | null;
}): boolean {
  const { isBroker, status, contractType, canBrokerDecline } = options;

  if (!isBroker) return false;
  if (!SELF_FINANCE_CONTRACT_TYPES.includes(contractType ?? "")) return false;
  if (!["Approved", "Remote Approved"].includes(status ?? "")) return false;

  return Boolean(canBrokerDecline);
}

const CARRIER_FIELDS_LOCKED_STATUSES = ["Pending", "Fuel Advance"];

export function isCarrierFRFieldsLocked(options: { isCarrier: boolean; status?: string }): boolean {
  const { isCarrier, status } = options;
  return isCarrier && CARRIER_FIELDS_LOCKED_STATUSES.includes(status ?? "");
}

export function canCancelCarrierPayment(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  canCancelCarrierPayment?: boolean | null;
}): boolean {
  const { isBroker, status, contractType, canCancelCarrierPayment } = options;

  if (!isBroker) return false;
  if (contractType !== FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM)
    return false;
  if (!["Approved", "Remote Approved"].includes(status ?? "")) return false;

  return Boolean(canCancelCarrierPayment);
}

const OFF_PLATFORM_OPEN_RECEIVABLE_STATUS = "off_platform_open";
const OPEN_RECEIVABLE_STATUS = "open";
const SELF_FINANCE_OPEN_RECEIVABLE_STATUS = "self_finance_open";

const APPROVED_STATUSES = ["Approved", "Remote Approved"];
const PAID_STATUS = "Paid";

// HP-9057: the shipper invoice amount stays correctable after the carrier is paid — the real guard
// is the receivable status, not the funding request status. Carrier fields deliberately keep the
// Approved-only list: the API rejects carrier edits once the FR is Paid.
const SHIPPER_AMOUNT_EDITABLE_STATUSES = [...APPROVED_STATUSES, PAID_STATUS];

// HP-9057: a broker save must never carry a status the broker cannot transition out of on this
// form, and Paid fails loudly rather than quietly — FR_STATUS maps the Paid option to the value
// "paid", which is not a FundingRequestStatusEnum member, so CreateOrUpdateBrokerFundingRequest
// rejects the whole mutation with "Invalid status 'paid'." before the serializer ever runs, taking
// the shipper invoice amount correction down with it. canBrokerEditApprovedFactoredFR covers the
// approved case and deliberately stays Approved-only: it also unlocks carrier fields in
// CarrierInformation.tsx, which the API rejects after payment.
export function shouldOmitStatusFromBrokerUpdate(status?: string): boolean {
  return status === PAID_STATUS;
}

// HP-8865: the shipper/invoice amount is editable while the receivable is fully open (no payments):
// off-platform uses its own OFF_PLATFORM_OPEN status; on-platform Self-Finance With Invoice is
// CF-held (HaulPay's AR). HP-9089 gave it its own SELF_FINANCE_OPEN status; the legacy OPEN value is
// still accepted for rows not yet backfilled (HP-9099).
export function canBrokerEditApprovedSelfFinanceShipperAmount(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  receivableStatus?: string | null;
}): boolean {
  const { isBroker, status, contractType, receivableStatus } = options;

  const isEditableStatus = isBroker && SHIPPER_AMOUNT_EDITABLE_STATUSES.includes(status ?? "");
  if (!isEditableStatus) return false;

  if (contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM) {
    return receivableStatus === OFF_PLATFORM_OPEN_RECEIVABLE_STATUS;
  }
  if (contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE) {
    return (
      receivableStatus === SELF_FINANCE_OPEN_RECEIVABLE_STATUS ||
      receivableStatus === OPEN_RECEIVABLE_STATUS
    );
  }
  return false;
}

// HP-9058: PO # and Client Invoice # print on the generated invoice, so what locks them is the
// receivable, not the funding request status. This is the carve-out that re-enables them once the
// form is globally locked, so it carries the same invoice-bearing status set as the shipper amount
// — before approval both fields are already editable through the normal form path, and a status
// like Declined or Deleted must not be unlocked by the receivable alone.
//
// Within those statuses the receivable is the authority, and a missing one means editable: there is
// nothing for an off-platform payment to have matched against yet. That deliberately differs from
// canBrokerEditApprovedSelfFinanceShipperAmount's bare equality check, which returns false for a
// missing receivable — do not collapse the two.
export function canBrokerEditSelfFinanceInvoiceReferenceFields(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  receivableStatus?: string | null;
}): boolean {
  const { isBroker, status, contractType, receivableStatus } = options;

  if (!isBroker) return false;
  if (contractType !== FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM) {
    return false;
  }
  if (!SHIPPER_AMOUNT_EDITABLE_STATUSES.includes(status ?? "")) return false;

  return receivableStatus == null || receivableStatus === OFF_PLATFORM_OPEN_RECEIVABLE_STATUS;
}

const SELF_FINANCE_CARRIER_EDIT_TYPES: string[] = [
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE,
  FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_NO_INVOICE
];

export function canBrokerEditApprovedSelfFinanceCarrierFields(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  carrierEditLocked?: boolean | null;
  carrierFundingCommitted?: boolean | null;
}): boolean {
  const { isBroker, status, contractType, carrierEditLocked, carrierFundingCommitted } = options;

  const isApprovedSelfFinance =
    isBroker &&
    SELF_FINANCE_CARRIER_EDIT_TYPES.includes(contractType ?? "") &&
    APPROVED_STATUSES.includes(status ?? "");

  if (!isApprovedSelfFinance) return false;

  // HP-9122: two independent locks. carrierEditLocked comes from the contract; committed funding is
  // its own signal the API already rejects on. Undefined means not committed, so records that
  // predate the flag are not newly locked out.
  return !carrierEditLocked && !carrierFundingCommitted;
}

export function canBrokerEditApprovedSelfFinanceOffPlatformFR(options: {
  isBroker: boolean;
  status?: string;
  contractType?: string;
  carrierEditLocked?: boolean | null;
  carrierFundingCommitted?: boolean | null;
  receivableStatus?: string | null;
}): { canEditCarrierFields: boolean; canEditShipperAmount: boolean } {
  const {
    isBroker,
    status,
    contractType,
    carrierEditLocked,
    carrierFundingCommitted,
    receivableStatus
  } = options;

  const isOffPlatform =
    isBroker &&
    contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM;

  if (!isOffPlatform) {
    return { canEditCarrierFields: false, canEditShipperAmount: false };
  }

  // HP-9057: the two flags no longer share a status gate. Carrier fields stop at Approved because
  // the API rejects them after payment; the shipper amount continues into Paid, guarded only by the
  // receivable still being open.
  // HP-9122: carrierFundingCommitted gates carrier fields only. The shipper amount stays editable
  // on a finalized FR while the receivable is open -- HP-9057 decoupled it deliberately, and the
  // API still accepts it after the carrier is paid.
  return {
    canEditCarrierFields:
      APPROVED_STATUSES.includes(status ?? "") && !carrierEditLocked && !carrierFundingCommitted,
    canEditShipperAmount:
      SHIPPER_AMOUNT_EDITABLE_STATUSES.includes(status ?? "") &&
      receivableStatus === OFF_PLATFORM_OPEN_RECEIVABLE_STATUS
  };
}

// HP-9100: single source of truth for "will the approved off-platform self-finance edit
// confirmation (the richer, recalculated-rates modal) fire for this edit?" Mirrors submitFR's own
// check exactly, so RightActions.tsx's separate carrier/shipper-edit confirmation can suppress
// itself for precisely the case the off-platform modal already covers, without the two drifting
// apart again.
export function isApprovedSelfFinanceOffPlatformEdit(options: {
  contractType?: string;
  status?: string;
  carrierAmountChanged: boolean;
  shipperAmountChanged: boolean;
  payoutDaysChanged: boolean;
  brokerSplitChanged: boolean;
}): boolean {
  const {
    contractType,
    status,
    carrierAmountChanged,
    shipperAmountChanged,
    payoutDaysChanged,
    brokerSplitChanged
  } = options;

  // HP-9057: Paid belongs here for the same reason the shipper amount reaches into it — an amount
  // correction after the carrier is paid is exactly the edit this confirmation exists for. Carrier
  // fields cannot reach this on a Paid FR: their inputs stay disabled, so the three carrier flags
  // are always false there and only shipperAmountChanged can open the modal.
  const isApprovedOffPlatform =
    contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM &&
    SHIPPER_AMOUNT_EDITABLE_STATUSES.includes(status ?? "");

  if (!isApprovedOffPlatform) return false;

  return carrierAmountChanged || shipperAmountChanged || payoutDaysChanged || brokerSplitChanged;
}

// HP-9058: the shipper invoice amount is no longer the only edit that reissues the invoice — PO #
// and Client Invoice # are printed on it too, so all three regenerate it and all three have to
// offer the resend. Returns the prompt copy naming what actually changed, or null when there is
// nothing to resend for. The status gate matches the serializer's: only an approved or paid FR has
// a generated invoice to reissue.
export function getOffPlatformResendInvoicePrompt(options: {
  contractType?: string;
  status?: string;
  shipperAmountChanged: boolean;
  poNumberChanged: boolean;
  clientInvoiceNumberChanged: boolean;
}): string | null {
  const {
    contractType,
    status,
    shipperAmountChanged,
    poNumberChanged,
    clientInvoiceNumberChanged
  } = options;

  const isInvoicedOffPlatformFR =
    contractType === FUNDING_REQUEST_CONTRACT_TYPES.SELF_FINANCE_WITH_INVOICE_OFF_PLATFORM &&
    SHIPPER_AMOUNT_EDITABLE_STATUSES.includes(status ?? "");
  if (!isInvoicedOffPlatformFR) return null;

  const changedLabels = [
    shipperAmountChanged ? "shipper invoice amount" : null,
    poNumberChanged ? "PO #" : null,
    clientInvoiceNumberChanged ? "Client Invoice #" : null
  ].filter((label): label is string => label !== null);
  if (!changedLabels.length) return null;

  const changedList =
    changedLabels.length > 1
      ? `${changedLabels.slice(0, -1).join(", ")} and ${changedLabels[changedLabels.length - 1]}`
      : changedLabels[0];

  return `The ${changedList} changed and the invoice was regenerated. Would you like to resend it to the debtor now?`;
}
